import { Pencil, X } from 'lucide-react';
import {
  type Event,
  type EventType,
  type User,
  EventStatus,
  EVENT_STATUS_LABELS,
  isAdmin,
} from '@peles/shared';
import { useViewMode } from '../../context/ViewModeContext';
import { useDialog } from '../../context/DialogContext';

interface EventWithType extends Event {
  eventTypeName?: string;
}

interface EventTableProps {
  events: EventWithType[];
  eventTypes: EventType[];
  selectedEventId: string | null;
  onSelectEvent: (id: string) => void;
  isLoading: boolean;
  currentUser?: User;
  onCancelEvent?: (id: string) => void;
}

const STATUS_BADGE_COLORS: Record<EventStatus, string> = {
  [EventStatus.DRAFT]: 'bg-gray-100 text-gray-700',
  [EventStatus.PLANNING]: 'bg-blue-100 text-blue-700',
  [EventStatus.CONFIRMED]: 'bg-green-100 text-green-700',
  [EventStatus.IN_PROGRESS]: 'bg-yellow-100 text-yellow-800',
  [EventStatus.COMPLETED]: 'bg-emerald-100 text-emerald-700',
  [EventStatus.CANCELLED]: 'bg-red-100 text-red-700',
};

export function EventTable({
  events,
  eventTypes,
  selectedEventId,
  onSelectEvent,
  isLoading,
  currentUser,
  onCancelEvent,
}: EventTableProps) {
  const { confirm } = useDialog();

  if (isLoading) {
    return <div className="text-gray-500 text-sm py-8 text-center">Loading events...</div>;
  }

  if (events.length === 0) {
    return <div className="text-gray-500 text-sm py-8 text-center">No events found</div>;
  }

  const { isMobile } = useViewMode();
  const currentUserIsAdmin = currentUser && isAdmin(currentUser.systemRole);

  const getEventTypeName = (eventTypeId: string): string => {
    const eventType = eventTypes.find((et) => et.id === eventTypeId);
    return eventType?.name || 'Unknown';
  };

  if (isMobile) {
    return (
      <div className="space-y-3">
        {events.map((event) => (
          <div
            key={event.id}
            onClick={() => onSelectEvent(event.id)}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
              selectedEventId === event.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex justify-between items-start mb-1">
              <span className="font-medium text-sm">{event.name}</span>
              <span
                className={`text-xs px-2 py-0.5 rounded shrink-0 ml-2 ${STATUS_BADGE_COLORS[event.status]}`}
              >
                {EVENT_STATUS_LABELS[event.status]}
              </span>
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>{new Date(event.startAt).toLocaleDateString()}</div>
              <div>{event.eventTypeName || getEventTypeName(event.eventTypeId)}</div>
              {event.venueName && <div>{event.venueName}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-500">
            <th className="pb-3 pr-4 font-medium">Name</th>
            <th className="pb-3 pr-4 font-medium">Type</th>
            <th className="pb-3 pr-4 font-medium">Date</th>
            <th className="pb-3 pr-4 font-medium">Venue</th>
            <th className="pb-3 pr-4 font-medium">Guests</th>
            <th className="pb-3 pr-4 font-medium">Budget</th>
            <th className="pb-3 pr-4 font-medium">Status</th>
            <th className="pb-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr
              key={event.id}
              onClick={() => onSelectEvent(event.id)}
              className={`border-b border-gray-100 cursor-pointer transition-colors ${
                selectedEventId === event.id ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
            >
              <td className="py-3 pr-4 font-medium">{event.name}</td>
              <td className="py-3 pr-4 text-gray-600">
                {event.eventTypeName || getEventTypeName(event.eventTypeId)}
              </td>
              <td className="py-3 pr-4 text-gray-600">
                {new Date(event.startAt).toLocaleDateString()}
              </td>
              <td className="py-3 pr-4 text-gray-600">{event.venueName || '\u2014'}</td>
              <td className="py-3 pr-4 text-gray-600">
                {event.guestCount != null ? event.guestCount.toLocaleString() : '\u2014'}
              </td>
              <td className="py-3 pr-4 text-gray-600">
                {event.budget != null
                  ? event.budget.toLocaleString('en-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    })
                  : '\u2014'}
              </td>
              <td className="py-3 pr-4">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE_COLORS[event.status]}`}
                >
                  {EVENT_STATUS_LABELS[event.status]}
                </span>
              </td>
              <td className="py-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectEvent(event.id);
                    }}
                    className="text-gray-400 hover:text-primary-600 p-1"
                    aria-label="Edit event"
                    title="Edit event"
                  >
                    <Pencil size={16} />
                  </button>
                  {currentUserIsAdmin && event.status !== EventStatus.CANCELLED && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          await confirm({
                            title: 'Cancel Event',
                            message: `Cancel event "${event.name}"? This action cannot be undone.`,
                            confirmLabel: 'Cancel Event',
                            cancelLabel: 'Keep Event',
                          })
                        ) {
                          onCancelEvent?.(event.id);
                        }
                      }}
                      className="text-gray-400 hover:text-red-600 p-1"
                      aria-label="Cancel event"
                      title="Cancel event"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
